/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'underscore',
	'../../../../../lib/@waca/dashboard-common/dist/utils/MergeUtil',
	'../../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../PropertiesAPI'
], (_, MergeUtil, APIFactory, PropertiesAPI) => {

	class Properties {
		constructor(options) {
			this._api = APIFactory.createAPI(this, [PropertiesAPI]);
			this.dashboard = options.features['Dashboard.API'];

			/**
			 * this Properties implementation is been used for both as Dashboard-feature and Content-feature.
			 * this.content will bu undefined if this is a dashboard feature instance.
			 */
			this.content = options.content;
			this._providers = [];
		}

		getAPI() {
			return this._api;
		}

		registerProvider(provider) {
			this._providers.push(provider);
		}

		deregisterProvider(provider) {
			const idx = this._providers.findIndex(p => p === provider);
			if ( idx !== -1 ) {
				this._providers.splice(idx, 1);
			}
		}

		getProviderPropertyNameList() {
			const providerProperties = {};
			let ind = 1;
			_.each(this._providers, (provider) => {
				let impltype;
				if (provider.getImplType) {
					impltype = provider.getImplType();
				} else {
					impltype = 'someViewClass_' + ind;
					ind += 1;
				}
				if (!providerProperties[impltype]) {
					providerProperties[impltype] = [];
				}
				if (provider.getPropertyList) {
					providerProperties[impltype].push(...provider.getPropertyList().map(prop=>prop.id));
				}
			});
			return providerProperties;
		}

		getPropertyList() {
			const allProperties = {};
			try {
				for( let i =0; i < this._providers.length; i++) {
					let provider = this._providers[i];
					if (!provider.isEnabled || provider.isEnabled()) {
						const properties = provider.getPropertyList ? provider.getPropertyList() : [];
						if( properties ) {
							for( let j=0; j< properties.length; j++) {
								let property = properties[j];
								this.modifyPropertyWithCallback(property);
								if (allProperties[property.id]) {
									this._mergeProperty(allProperties, property.id, property);
								} else {
									allProperties[property.id] = property;
								}
							}
						}
					}
				}
			} catch (e) {
				// Do nothing since all error should be logged by render sequence
				console.log(e);
			}
			this._filterUnsupportedProperties(allProperties);
			return _.values(allProperties);
		}

		/**
		 * IMPORTANT:
		 *  - removes showTitle or titleMode based on the config property named smartTitle
		 *  - the same logic is in WidgetTitleView
		 *  - check the dashboardSettings feature for unit test purpose
		 * @param {object} allProps
		 * @returns {object} filtered properties
		 */
		_filterUnsupportedProperties(allProps) {
			if(this.dashboard) {
				const dashboardSettings = this.dashboard.getFeature('DashboardSettings');
				let titleModeEnabled = false;
				if (dashboardSettings) {
					const applicationSettings = dashboardSettings.get('applicationSettings');
					titleModeEnabled = applicationSettings
										&& applicationSettings.options
										&& applicationSettings.options.config
										&& applicationSettings.options.config.smartTitle === true;
				}
				if (titleModeEnabled) {
					delete allProps.showTitle;
				} else {
					delete allProps.titleMode;
				}
			}
			return allProps;
		}

		getPropertyLayoutList() {
			const allLayouts = {};
			_.each(this._providers, (provider) => {
				if (!provider.isEnabled || provider.isEnabled()) {
					const layouts = provider.getPropertyLayoutList ? provider.getPropertyLayoutList() || [] : [];
					_.each(layouts, (layout) => {
						if (allLayouts[layout.id]) {
							this._mergeProperty(allLayouts, layout.id, layout);
						} else {
							allLayouts[layout.id] = layout;
						}
					});
				}
			});
			return _.values(allLayouts);
		}

		_mergeProperty(properties, id, provided) {
			const current = properties[id];
			if(provided.override === false) {
				return;
			}
			const currentProfile = current.profile && [...current.profile];

			properties[id] = MergeUtil.mergeJson(current, provided);

			if (currentProfile && provided.profile) {
				properties[id].profile.push(...currentProfile);
				properties[id].profile = _.uniq(properties[id].profile);
			}
		}

		modifyPropertyWithCallback(propObj) {
			for (const key in propObj) {
				if (key.indexOf('Callback') !== -1 || key === 'onChange') {
					const defCallback = propObj[key];
					if (typeof defCallback === 'function') {
						return;
					}
					if (typeof defCallback === 'string') {
						/// todo: should not have this case
						console.error('callback in definition should use new property callback method.');
						return;
					}
					try {
						const featurePath = defCallback.feature;
						let feature = null;
						if (featurePath.indexOf('Dashboard.') === 0) {
							const featureName = featurePath.split('.')[1];
							feature = this.dashboard.getFeature(featureName);
						}
						if (!feature) {
							feature = this.content.getFeature(featurePath);
						}
						const params = [this.content];
						params.push(...defCallback.callbackParams);
						propObj[key] = feature[defCallback.callbackFunction].bind(feature, ...params);
					} catch (error) {
						console.error(error);
					}
				} else {
					if (typeof propObj[key] == 'object' && propObj[key] !== null) {
						this.modifyPropertyWithCallback(propObj[key]);
					}
				}
			}
		}
	}

	return Properties;
});
